import { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { dateToLocalISO } from '@/lib/format';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Combobox } from '@/components/ui/combobox';
import { formatCurrency } from '@/lib/utils';
import { LineItemData } from './LineItemList';
import { useLineItemCategories } from '@/hooks/useConfigurableLists';
import {
  type SubItemForm,
  type LaborSubItemForm,
  type DirectSubItemForm,
  isQtyRateType,
  createSubItem,
  getSubItemLabel,
  BUILT_IN_KEYS,
} from './subItemTypes';
import {
  calculateLaborCost,
  calculateTypeTotal,
  getCostBreakdown,
  getUnitLabel,
  type Rates,
  DEFAULT_TAX_RATE,
  DEFAULT_MARKUP_RATE,
} from '@jobos/shared';
import { MarkupRateBadge } from './MarkupRateBadge';
import { LaborRateSelect } from './LaborRateSelect';
import { UnitLabelInput } from '@/components/shared/UnitLabelInput';
import { useSubItemCategoriesContext, useSubItemTypeMeta } from '@/contexts/SubItemCategoriesContext';
import { CarpetLineItemBody } from '../carpet/CarpetLineItemBody';
import { EquipmentLineItemBody } from '../equipment/EquipmentLineItemBody';

// Deterministic color cycle for category badges
const BADGE_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-amber-100 text-amber-700',
  'bg-green-100 text-green-700',
  'bg-purple-100 text-purple-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
];

// Editable currency cell component
function CurrencyCell({
  value,
  onChange,
  placeholder = '0.00',
  isNegative = false,
  disabled = false,
}: {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  isNegative?: boolean;
  disabled?: boolean;
}) {
  const [localValue, setLocalValue] = useState(value ? value.toFixed(2) : '');
  const [isFocused, setIsFocused] = useState(false);

  // Sync with external value when not focused
  if (!isFocused && value !== parseFloat(localValue || '0')) {
    if (value === 0 && localValue === '') {
      // Keep empty for zero
    } else if (value.toFixed(2) !== localValue) {
      setLocalValue(value ? value.toFixed(2) : '');
    }
  }

  const isEmpty = value === 0 && !isFocused;

  // When disabled, just show the formatted value
  if (disabled) {
    return (
      <div className={`text-xs tabular-nums text-right ${isEmpty ? 'opacity-30' : ''}`}>
        {isNegative && value > 0 ? '-' : ''}
        {formatCurrency(value)}
      </div>
    );
  }

  return (
    <div className={`relative transition-opacity ${isEmpty ? 'opacity-30' : ''}`}>
      <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
        {isNegative ? '-$' : '$'}
      </span>
      <Input
        type="number"
        min="0"
        step="0.01"
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          const val = parseFloat(e.target.value);
          onChange(isNaN(val) ? 0 : Math.round(val * 100) / 100);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          const val = parseFloat(e.target.value);
          setLocalValue(isNaN(val) || val === 0 ? '' : val.toFixed(2));
        }}
        placeholder={placeholder}
        className={`h-7 w-20 ${isNegative ? 'pl-5' : 'pl-4'} pr-1 text-right text-xs tabular-nums bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      />
    </div>
  );
}

export interface BreakdownTableRowProps {
  item: LineItemData;
  itemNumber: number;
  onUpdate: (updates: Partial<LineItemData>) => void;
  onDelete: () => void;
  readOnly?: boolean;
  taxRate?: number;
  markupRate?: number;
  checked?: boolean;
  onCheck?: (id: string, checked: boolean) => void;
  isExpanded?: boolean;
  onToggleExpand?: (id: string) => void;
}

export function BreakdownTableRow({
  item,
  itemNumber,
  onUpdate,
  onDelete,
  readOnly = false,
  taxRate = DEFAULT_TAX_RATE,
  markupRate = DEFAULT_MARKUP_RATE,
  checked,
  onCheck,
  isExpanded = false,
  onToggleExpand,
}: BreakdownTableRowProps) {
  const { categories } = useSubItemCategoriesContext();
  const typeMeta = useSubItemTypeMeta();
  const { data: lineItemCategories } = useLineItemCategories();
  const categoryOptions = lineItemCategories.map((c) => ({ value: c, label: c }));
  const rates: Rates = { taxRate, markupRate };
  const [editingCategory, setEditingCategory] = useState(false);
  const toggleExpanded = () => onToggleExpand?.(item.id);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Categories for column rendering
  const dataCols = categories.filter(
    (cat) => cat.key !== BUILT_IN_KEYS.PROFIT && cat.key !== BUILT_IN_KEYS.DISCOUNT
  );
  const qtyRateCategories = categories.filter((cat) => cat.pricingMode === 'qty_rate');
  const amountCategories = categories.filter(
    (cat) => cat.pricingMode === 'amount' && cat.key !== BUILT_IN_KEYS.PROFIT && cat.key !== BUILT_IN_KEYS.DISCOUNT
  );

  const allQtyRateItems = item.subItems.filter(
    (si): si is LaborSubItemForm => isQtyRateType(si.type, typeMeta)
  );
  const laborTotal = calculateLaborCost(item.subItems);

  const getTypeTotal = (type: string) => calculateTypeTotal(item.subItems, type, typeMeta);
  const breakdown = getCostBreakdown(item.subItems, rates, typeMeta, categories);

  // Helper to update a specific sub-item type's amount (for direct/amount types only)
  const updateDirectSubItem = (type: string, amount: number) => {
    const existingItems = item.subItems.filter(
      (si): si is DirectSubItemForm => si.type === type
    );

    let newSubItems: SubItemForm[];
    if (existingItems.length === 0) {
      if (amount > 0) {
        const newItem = createSubItem(type, typeMeta) as DirectSubItemForm;
        newItem.amount = amount;
        newSubItems = [...item.subItems, newItem];
      } else {
        return;
      }
    } else if (existingItems.length === 1) {
      const existingId = existingItems[0]!.id;
      newSubItems = item.subItems.map((si) =>
        si.id === existingId ? { ...si, amount } : si
      );
    } else {
      const currentTotal = existingItems.reduce((sum, si) => sum + si.amount, 0);

      if (amount === 0) {
        newSubItems = item.subItems.filter((si) => si.type !== type);
      } else if (currentTotal === 0) {
        const perItem = amount / existingItems.length;
        const existingIds = new Set(existingItems.map((si) => si.id));
        newSubItems = item.subItems.map((si) =>
          existingIds.has(si.id) ? { ...si, amount: perItem } : si
        );
      } else {
        const ratio = amount / currentTotal;
        const existingIds = new Set(existingItems.map((si) => si.id));
        newSubItems = item.subItems.map((si) =>
          existingIds.has(si.id) ? { ...si, amount: Math.round((si as DirectSubItemForm).amount * ratio * 100) / 100 } : si
        );
      }
    }
    onUpdate({ subItems: newSubItems });
  };

  // Add a new qty-rate item of the given type
  const addQtyRateItem = (type: string) => {
    const newItem = createSubItem(type, typeMeta) as LaborSubItemForm;
    onUpdate({ subItems: [...item.subItems, newItem] });
  };

  // Update a sub-item by id
  const updateSubItemById = (id: string, updates: Record<string, any>) => {
    const newSubItems = item.subItems.map((si) =>
      si.id === id ? { ...si, ...updates } as typeof si : si
    );
    onUpdate({ subItems: newSubItems });
  };

  // Delete a sub-item by id
  const deleteSubItemById = (id: string) => {
    onUpdate({ subItems: item.subItems.filter((si) => si.id !== id) });
  };

  // Add a new amount item for a given type
  const addAmountItem = (type: string) => {
    const newItem = createSubItem(type, typeMeta) as DirectSubItemForm;
    onUpdate({ subItems: [...item.subItems, newItem] });
  };

  const isDisclaimer = item.category.toLowerCase() === 'disclaimer';

  // Get badge color for a category key
  const getBadgeColor = (key: string): string => {
    const idx = categories.findIndex((c) => c.key === key);
    return BADGE_COLORS[idx >= 0 ? idx % BADGE_COLORS.length : 0] ?? BADGE_COLORS[0]!;
  };

  // Total colspan for expanded detail row
  const totalColSpan = dataCols.length + 6; // drag + category + description + dataCols + profit + discount + total

  // Render the combined qty-rate section in the expanded detail row
  const renderQtyRateSection = () => (
    <div className="min-w-0">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Labor Entries
      </h4>
      <div className="space-y-2">
        {allQtyRateItems.map((entry) => {
          const abbr = getUnitLabel({
            unitKey: entry.unitKey,
            categoryUnitLabel: typeMeta[entry.type]?.unitLabel ?? null,
            rateType: entry.rateType,
          });
          return (
          <div key={entry.id} className="flex items-start gap-2">
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded shrink-0 mt-1 ${getBadgeColor(entry.type)}`}>
              {getSubItemLabel(entry.type, typeMeta)}
            </span>
            {readOnly ? (
              <span className="text-xs tabular-nums w-16 text-center mt-1.5">{entry.hours}</span>
            ) : (
              <Input
                type="number"
                min="0"
                step="0.5"
                value={entry.hours || ''}
                onChange={(e) => updateSubItemById(entry.id, { hours: parseFloat(e.target.value) || 0 })}
                placeholder="Qty"
                className="h-7 w-16 px-2 text-xs text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            )}
            {readOnly ? (
              <span className="text-xs text-gray-500 w-16 text-center mt-1.5">{abbr}</span>
            ) : (
              <div className="w-20" onClick={(e) => e.stopPropagation()}>
                <UnitLabelInput
                  value={entry.unitKey ?? ''}
                  onChange={(v) => updateSubItemById(entry.id, { unitKey: v || undefined })}
                  placeholder={abbr}
                  className="h-7 text-xs"
                />
              </div>
            )}
            <span className="text-xs text-gray-400 mt-1.5"> ×</span>
            <div className="w-36" onClick={(e) => e.stopPropagation()}>
              <LaborRateSelect
                value={entry.rate}
                unitKey={entry.unitKey}
                onChange={(rate, unitKey) => updateSubItemById(entry.id, { rate, ...(unitKey !== undefined && { unitKey }) })}
                disabled={readOnly}
                category={entry.type}
              />
            </div>
            <span className="text-xs text-gray-400 mt-1.5"> =</span>
            <div className="flex flex-col items-end">
              <span className="text-sm font-medium tabular-nums w-20 leading-7">
                {formatCurrency(entry.hours * entry.rate)}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-gray-400">Markup:</span>
                <MarkupRateBadge
                  markupRate={entry.markupRate}
                  jobMarkupRate={markupRate}
                  onChange={(rate) => updateSubItemById(entry.id, { markupRate: rate })}
                  readOnly={readOnly}
                />
              </div>
            </div>
            {!readOnly && (
              <button
                onClick={() => deleteSubItemById(entry.id)}
                className="p-1 mt-0.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          );
        })}
        {allQtyRateItems.length === 0 && (
          <p className="text-xs text-gray-400 italic">No labor entries</p>
        )}
      </div>
      {!readOnly && (
        <div className="flex items-center gap-3 mt-2">
          {qtyRateCategories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => addQtyRateItem(cat.key)}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
            >
              + Add {cat.label.toLowerCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Render amount entries section (materials and other amount types with multiple items)
  const renderAmountSection = (type: string, label: string) => {
    const items = item.subItems.filter(
      (si): si is DirectSubItemForm => si.type === type
    );

    return (
      <div className="min-w-0">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          {label} Entries
        </h4>
        <div className="space-y-2">
          {items.map((mat, idx) => (
            <div key={mat.id} className="flex items-start gap-2">
              <span className="text-xs text-gray-500 w-8 shrink-0 mt-1.5">#{idx + 1}</span>
              <div className="flex flex-col items-end">
                {readOnly ? (
                  <span className="text-xs tabular-nums w-24 text-right leading-7">{formatCurrency(mat.amount)}</span>
                ) : (
                  <div className="relative">
                    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      $
                    </span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={mat.amount || ''}
                      onChange={(e) => updateSubItemById(mat.id, { amount: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="h-7 w-24 pl-5 pr-2 text-xs text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                )}
                {type === 'materials' && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-gray-400">Markup:</span>
                    <MarkupRateBadge
                      markupRate={mat.markupRate}
                      jobMarkupRate={markupRate}
                      onChange={(rate) => updateSubItemById(mat.id, { markupRate: rate })}
                      readOnly={readOnly}
                    />
                  </div>
                )}
              </div>
              {readOnly ? (
                mat.note && <span className="text-xs text-gray-400 italic mt-1.5">{mat.note}</span>
              ) : (
                <input
                  type="text"
                  value={mat.note || ''}
                  onChange={(e) => updateSubItemById(mat.id, { note: e.target.value })}
                  placeholder="Add note..."
                  className="text-xs text-gray-600 bg-transparent border-none outline-none focus:text-gray-700 placeholder:text-gray-400 w-40 mt-1.5"
                />
              )}
              {!readOnly && (
                <button
                  onClick={() => deleteSubItemById(mat.id)}
                  className="p-1 mt-0.5 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-xs text-gray-400 italic">No {label.toLowerCase()} entries</p>
          )}
        </div>
        {!readOnly && (
          <button
            onClick={() => addAmountItem(type)}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800 transition-colors"
          >
            + Add {label.toLowerCase()} entry
          </button>
        )}
      </div>
    );
  };

  // Render a clickable expandable total cell for qty-rate types
  const renderQtyRateCell = (total: number, count: number) => (
    <button
      onClick={() => !readOnly && toggleExpanded()}
      className={`flex items-center gap-1 transition-opacity ${!readOnly ? 'hover:opacity-100 cursor-pointer' : 'cursor-default'} ${total === 0 ? 'opacity-30' : ''}`}
    >
      <span className="text-xs font-medium tabular-nums">
        {formatCurrency(total)}
      </span>
      {count > 0 && (
        <span className="text-xs text-blue-500">({count})</span>
      )}
    </button>
  );

  if (isDisclaimer) {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-t border-gray-200 hover:bg-gray-50 ${isDragging ? 'opacity-50 bg-blue-50' : ''}`}
      >
        <td className="py-2 pl-2 pr-1 w-8">
          <div className="flex items-center gap-0.5">
            {onCheck && (
              <input
                type="checkbox"
                checked={checked ?? false}
                onChange={(e) => onCheck(item.id, e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            )}
            {!readOnly && (
              <button
                className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
        <td className="py-2 px-3">
          <span className="text-sm font-medium text-gray-700">{item.category}</span>
        </td>
        <td className="py-2 px-2 min-w-48">
          <span className="text-xs text-gray-600 line-clamp-2">{item.description}</span>
        </td>
        <td colSpan={dataCols.length + 2} className="py-2 px-2 text-center text-xs text-gray-400">
          —
        </td>
        <td className="py-2 px-2 text-right">
          {!readOnly && (
            <button
              onClick={onDelete}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-t border-gray-200 ${isExpanded ? 'bg-blue-50 hover:bg-blue-50' : 'hover:bg-gray-50'} ${isDragging ? 'opacity-50 bg-blue-50' : ''}`}
      >
        {/* Checkbox + Drag handle + number */}
        <td className={`py-2 pl-2 pr-1 w-8 ${isExpanded ? 'border-l-4 border-blue-500' : ''}`}>
          <div className="flex items-center gap-0.5">
            {onCheck && (
              <input
                type="checkbox"
                checked={checked ?? false}
                onChange={(e) => onCheck(item.id, e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            )}
            {!readOnly && (
              <button
                className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
            <span className="text-xs font-semibold text-gray-400">{itemNumber}</span>
          </div>
        </td>
        {/* Category */}
        <td className="py-2 px-3">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleExpanded}
              className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            {readOnly ? (
              <span className="text-sm font-medium text-gray-700 truncate max-w-40 block">
                {item.category || 'Untitled'}
              </span>
            ) : (
              <Popover onOpenChange={(open) => { if (!open) setEditingCategory(false); }}>
                <PopoverTrigger asChild>
                  <button
                    className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors truncate block text-left w-full py-1"
                    onClick={() => setEditingCategory(true)}
                  >
                    {item.category || <span className="italic text-gray-400">Select category</span>}
                  </button>
                </PopoverTrigger>
                {editingCategory && (
                  <PopoverContent align="start" className="w-56 p-2 [&_input:focus]:!ring-2 [&_input:focus]:!ring-inset [&_input:focus]:!ring-blue-500 [&_input:focus]:!outline-none [&_[role=combobox]:focus]:!ring-2 [&_[role=combobox]:focus]:!ring-blue-500 [&_[role=combobox]:focus]:!outline-none" onOpenAutoFocus={(e: Event) => e.preventDefault()}>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Combobox
                        value={item.category}
                        onValueChange={(val) => {
                          onUpdate({ category: val });
                          setEditingCategory(false);
                        }}
                        options={categoryOptions}
                        placeholder="Select category"
                        searchPlaceholder="Search categories..."
                        emptyText="No category found."
                        allowCustom
                        customLabel="Other"
                        defaultOpen
                      />
                    </div>
                  </PopoverContent>
                )}
              </Popover>
            )}
          </div>
        </td>

        {/* Description + Date */}
        <td className="py-2 px-2 min-w-48">
          {readOnly ? (
            <span className="text-xs text-gray-600 line-clamp-2">
              {item.itemDate && (
                <span className="text-gray-400">({new Date(item.itemDate + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}) </span>
              )}
              {item.description || <span className="italic text-gray-400">—</span>}
            </span>
          ) : (
            <Popover>
              <PopoverTrigger asChild>
                <button className="text-left w-full group">
                  <span className="text-xs text-gray-600 line-clamp-2 group-hover:text-gray-900 transition-colors">
                    {item.itemDate && (
                      <span className="text-gray-400">({new Date(item.itemDate + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}) </span>
                    )}
                    {item.description || <span className="italic text-gray-400">Add description...</span>}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-96 p-3 [&_input:focus]:!ring-2 [&_input:focus]:!ring-inset [&_input:focus]:!ring-blue-500 [&_input:focus]:!outline-none [&_textarea:focus]:!ring-2 [&_textarea:focus]:!ring-inset [&_textarea:focus]:!ring-blue-500 [&_textarea:focus]:!outline-none"
                onOpenAutoFocus={(e: Event) => e.preventDefault()}
              >
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Description
                    </label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => onUpdate({ description: e.target.value })}
                      placeholder="Enter description..."
                      className="min-h-[120px] text-sm resize-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date
                    </label>
                    <DatePicker
                      aria-label="Line item date"
                      value={item.itemDate || null}
                      placeholder="Optional"
                      onChange={(d) => onUpdate({ itemDate: d ? dateToLocalISO(d) : undefined })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </td>

        {/* Dynamic data columns */}
        {dataCols.map((cat) => {
          if (cat.pricingMode === 'qty_rate') {
            const total = cat.key === 'labor' ? laborTotal : getTypeTotal(cat.key);
            const count = item.subItems.filter((si) => si.type === cat.key).length;
            return (
              <td key={cat.key} className="py-2 px-2">
                {renderQtyRateCell(total, count)}
              </td>
            );
          }

          // Amount type
          const amountItems = item.subItems.filter(
            (si): si is DirectSubItemForm => si.type === cat.key
          );

          if (amountItems.length > 1) {
            return (
              <td key={cat.key} className="py-2 px-2">
                <button
                  onClick={() => !readOnly && toggleExpanded()}
                  className={`flex items-center gap-1 transition-opacity ${!readOnly ? 'hover:opacity-100 cursor-pointer' : 'cursor-default'} ${getTypeTotal(cat.key) === 0 ? 'opacity-30' : ''}`}
                >
                  <span className="text-xs font-medium tabular-nums">
                    {formatCurrency(getTypeTotal(cat.key))}
                  </span>
                  <span className="text-xs text-blue-500">({amountItems.length})</span>
                </button>
              </td>
            );
          }

          return (
            <td key={cat.key} className="py-2 px-2">
              <CurrencyCell
                value={getTypeTotal(cat.key)}
                onChange={(val) => updateDirectSubItem(cat.key, val)}
                disabled={readOnly}
              />
            </td>
          );
        })}

        {/* Profit */}
        <td className="py-2 px-2">
          <CurrencyCell
            value={getTypeTotal(BUILT_IN_KEYS.PROFIT)}
            onChange={(val) => updateDirectSubItem(BUILT_IN_KEYS.PROFIT, val)}
            disabled={readOnly}
          />
        </td>

        {/* Discount */}
        <td className="py-2 px-2">
          <CurrencyCell
            value={getTypeTotal(BUILT_IN_KEYS.DISCOUNT)}
            onChange={(val) => updateDirectSubItem(BUILT_IN_KEYS.DISCOUNT, val)}
            isNegative
            disabled={readOnly}
          />
        </td>

        {/* Total + Actions */}
        <td className="py-2 px-2">
          <div className="flex items-center justify-end gap-2">
            {breakdown.total !== 0 && (
              <span className="text-sm font-semibold tabular-nums text-gray-900">
                {formatCurrency(breakdown.total)}
              </span>
            )}
            {!readOnly && (
              <button
                onClick={onDelete}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Detail row - full width when expanded */}
      {isExpanded && (
        <tr className="bg-blue-50/40 border-b border-gray-200">
          <td colSpan={totalColSpan} className="py-4 pl-14 pr-10 border-l-4 border-blue-500">
            {item.category === 'Carpet Cleaning' ? (
              <CarpetLineItemBody
                subItems={item.subItems}
                onSubItemsChange={(next) => onUpdate({ subItems: next })}
                readOnly={readOnly}
              />
            ) : item.category === 'Equipment Rental' ? (
              <EquipmentLineItemBody
                subItems={item.subItems}
                onSubItemsChange={(next) => onUpdate({ subItems: next })}
                readOnly={readOnly}
                lineItemDescription={item.description ?? null}
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
                {/* All qty-rate entries */}
                {renderQtyRateSection()}

                {/* Amount entries sections for types with multiple items */}
                {amountCategories.map((cat) => {
                  const items = item.subItems.filter((si) => si.type === cat.key);
                  // Only show expanded section for types with items or materials (always show materials)
                  if (items.length === 0 && cat.key !== 'materials') return null;
                  return (
                    <div key={cat.key}>
                      {renderAmountSection(cat.key, cat.label)}
                    </div>
                  );
                })}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
